import React, { useState, useRef, createRef } from 'react';

import FloatingActionButton from '@ui/buttons/FloatingActionButton.tsx';
import MapNavButton from '@ui/buttons/MapNavButton.tsx';
import Tooltip from '@ui/tooltip';

import { useCSSColors } from '../../../../src/hooks/useCSSColors.ts';
import { widgetEnum } from '../../../../utils/constants.ts';
import type { PluginConfig } from '../config-schema.ts';

const MAP_SHORTENER_URL = 'https://map-url.locuslabs.com/get-shortened-map-url';

/**
 * Returns a shortened version of the given URL via the Atrius map URL shortener.
 * Returns null if shortening fails (network error, non-OK response, or malformed response).
 * @param {string} url
 * @returns {Promise<string | null>}
 */
const getShortenedUrl = async url => {
  try {
    const res = await fetch(MAP_SHORTENER_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ url }),
    });
    if (!res.ok) {
      return null;
    }
    const { shortUrl } = await res.json();
    return shortUrl || null;
  } catch {
    return null;
  }
};

const DEFAULT_INSTALL_ID = 'NO_INSTALL_ID';
const MAP_LINK_SHARE_BUTTON_DESKTOP = 'mapLinkShareButtonDesktop';
const MAP_LINK_SHARE_BUTTON_MOBILE = 'mapLinkShareButtonMobile';

const getShortenedMapUrl = async installId => {
  const pageUrl = `${window.location.href}&refInstallId=${installId}`;
  return getShortenedUrl(pageUrl);
};

const copyShareLink = async installId => {
  const urlPromise = getShortenedMapUrl(installId).then(url => {
    if (!url) {
      throw new Error('URL shortener failed');
    }
    return url;
  });

  // Targets Chrome 76+, Edge 79+, Safari 13.1+, Firefox 127+. Using Promise<Blob> is required for Safari async clipboard writes.
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    const blobPromise = urlPromise.then(url => new Blob([url], { type: 'text/plain' }));
    await navigator.clipboard.write([new ClipboardItem({ 'text/plain': blobPromise })]);
    return;
  }

  // Fallback for Firefox < 127, Chrome < 76, Edge < 79, and older browsers lacking ClipboardItem support.
  if (typeof navigator.clipboard?.writeText === 'function') {
    await navigator.clipboard.writeText(await urlPromise);
  } else {
    throw new Error('Clipboard API unavailable');
  }
};

const SHARING_BTN_STATE = {
  DEFAULT: 0,
  IN_PROGRESS: 1,
  SUCCESS: 2,
  ERROR: 3,
};

const LinkSharingButton = ({ T, installId, app }) => {
  const colors = useCSSColors();
  const [state, setState] = useState(SHARING_BTN_STATE.DEFAULT);

  const focusAfterToastDismissRef = useRef(null);

  const showSuccessMessage = () => {
    setState(SHARING_BTN_STATE.SUCCESS);
    app.bus.send('toast/show', {
      message: app.gt()('mapLinkShareButton:Link successfully copied to clipboard'),
      iconName: 'success',
      background: colors.statusSuccessBackground,
      focusAfterToastDismissRef,
    });
  };

  const showFailMessage = () => {
    setState(SHARING_BTN_STATE.ERROR);
    app.bus.send('toast/show', {
      message: app.gt()('mapLinkShareButton:Your link was not copied Please try again'),
      iconName: 'warning',
      background: colors.statusErrorBackground,
      focusAfterToastDismissRef,
    });
  };

  const onButtonClick = () => {
    setState(SHARING_BTN_STATE.IN_PROGRESS);
    copyShareLink(installId).then(showSuccessMessage).catch(showFailMessage);
  };

  if (state === SHARING_BTN_STATE.IN_PROGRESS) {
    return <MapNavButton iconName="external.link" loader />;
  }

  return (
    <>
      <Tooltip text={T('mapLinkShareButton:Copy link to clipboard')} arrowPosition="left">
        <MapNavButton
          ref={focusAfterToastDismissRef}
          aria-label={T('mapLinkShareButton:Copy link to clipboard')}
          iconName="external.link"
          onClick={onButtonClick}
          data-testid={'LinkSharingButton'}
        />
      </Tooltip>
    </>
  );
};

const createDesktopController = async app => {
  const installIdResponse = await app.bus.send('analytics2/getInstallId');
  const installId = installIdResponse && installIdResponse.length ? installIdResponse[0] : DEFAULT_INSTALL_ID;
  app.bus.send('layers/register', {
    id: MAP_LINK_SHARE_BUTTON_DESKTOP,
    widget: () => <LinkSharingButton T={app.gt()} installId={installId} app={app} />,
    show: true,
    isOverlay: true,
    layoutId: 'buttonOptions',
    widgetType: widgetEnum.Desktop,
    order: 40,
  });
};

const createMobileController = async app => {
  const T = app.gt();
  const installIdResponse = await app.bus.send('analytics2/getInstallId');
  const installId = installIdResponse && installIdResponse.length ? installIdResponse[0] : DEFAULT_INSTALL_ID;
  const focusAfterToastDismissRefMobile = createRef(null);

  const showSuccessMessage = () => {
    app.bus.send('toast/show', {
      message: T('mapLinkShareButton:Link successfully copied to clipboard'),
      iconName: 'success',
      background: 'var(--color-statusSuccessBackground)',
      color: 'var(--color-statusSuccessText)',
      focusAfterToastDismissRef: focusAfterToastDismissRefMobile,
    });
  };
  const showFailMessage = () => {
    app.bus.send('toast/show', {
      message: T('mapLinkShareButton:Your link was not copied Please try again'),
      iconName: 'delete.text',
      background: 'var(--color-statusErrorBackground)',
      color: 'var(--color-statusErrorText)',
      focusAfterToastDismissRef: focusAfterToastDismissRefMobile,
    });
  };
  const onButtonTap = () => {
    copyShareLink(installId).then(showSuccessMessage).catch(showFailMessage);
  };

  app.bus.send('layers/register', {
    id: MAP_LINK_SHARE_BUTTON_MOBILE,
    widget: () => (
      <FloatingActionButton
        ref={focusAfterToastDismissRefMobile}
        data-testid="mapLinkShareButton"
        iconName="external.link"
        onClick={onButtonTap}
        aria-label={T('mapLinkShareButton:Press to copy link to clipboard')}
      />
    ),
    shouldShow: currentlyShowing => {
      if (currentlyShowing.includes('SearchResultsBody') && currentlyShowing.includes('SearchResultsHeader')) {
        return true;
      }
      const widgetsToCheck = ['SearchResultsHeader'];
      return !widgetsToCheck.some(widget => currentlyShowing.includes(widget));
    },
    isOverlay: true,
    layoutId: 'mapControls',
    widgetType: widgetEnum.Mobile,
    order: 20,
  });
};

function create(app, config: PluginConfig) {
  const init = () => {
    app.bus.on('mapLinkShare/getShortUrl', async ({ url }) => {
      const shortUrl = await getShortenedUrl(url);
      return { url: shortUrl };
    });
  };

  if (!config.hide) {
    createDesktopController(app);
    createMobileController(app);
  }

  return {
    init,
  };
}

export { create };
